/**
 * features/finance/Budgets.tsx
 * ------------------------------------------------------------------
 * WHAT THE SCHOOL PLANNED TO SPEND, AND WHAT IT HAS.
 *
 * A budget is only useful in the middle of a year. At the end it is
 * history and at the start it is a wish; the question worth answering in
 * September is "at this point in the year, should we have spent this
 * much?" — which a percentage of the annual figure cannot answer. A
 * department that has used 90% of its budget is fine in November and in
 * trouble in March.
 *
 * SO THE HEADLINE IS THE VARIANCE TO DATE, and the annual figure sits
 * beside it rather than in front of it.
 *
 * OVER AND UNDER ARE NOT THE SAME NEWS. Overspending an expense is bad;
 * over-collecting income is good. The same arithmetic means opposite
 * things, so the colour comes from lib/budgets rather than from the sign
 * — a screen that paints both red teaches people to ignore the colour.
 *
 * PER ACCOUNT, NOT PER COST CENTRE. LKC has not defined cost centres,
 * and inventing an axis the school does not use would produce a report
 * nobody can act on. Accounts are what the chart already has.
 * ------------------------------------------------------------------ */
import { useEffect, useMemo, useState } from 'react';
import { collection, doc, onSnapshot, query } from 'firebase/firestore';
import {
  Target, AlertTriangle, TrendingUp, TrendingDown, Save, Info,
} from 'lucide-react';
import { Spinner } from '@/components/ui/primitives';
import { useAuth } from '@/contexts/AuthContext';
import { db, setDoc } from '@/lib/firebase';
import { FINANCE_COLLECTIONS } from '@/lib/financeCore';
import { useGlData } from '@/features/finance/useGlData';
import {
  BUDGET_COLLECTION, buildBudgetReport,
  type AccountBudget, type BudgetLine,
} from '@/lib/budgets';
import {
  FY_DEFAULT, periodsInFy, periodLabel, type FySettings,
} from '@/lib/financialYear';
import {
  PageHeader, SoftCard, PanelHeader, StatChip, glassFieldClass, btnPrimary, btnGhost,
} from '@/features/finance/ui';

const money = (n: number) =>
  n.toLocaleString('en-BW', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function Budgets() {
  const { userData } = useAuth();
  const thisYear = new Date().getFullYear();
  const [fy, setFy] = useState(`FY${thisYear}`);
  const gl = useGlData(fy);

  const [budgets, setBudgets] = useState<AccountBudget[] | null>(null);
  const [fySettings, setFySettings] = useState<FySettings>(FY_DEFAULT);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => onSnapshot(
    query(collection(db, BUDGET_COLLECTION)),
    (s) => setBudgets(s.docs.map((d) => ({ id: d.id, ...d.data() }) as AccountBudget)),
    () => setBudgets([]),
  ), []);
  useEffect(() => onSnapshot(
    doc(db, FINANCE_COLLECTIONS.settings, 'school'),
    (s) => setFySettings((s.data()?.financialYear ?? FY_DEFAULT) as FySettings),
    () => setFySettings(FY_DEFAULT),
  ), []);

  const months = useMemo(() => periodsInFy(fy, fySettings), [fy, fySettings]);

  /* Default to the month just finished. A budget report that includes the
     month in progress always looks favourable, because the spending has
     not happened yet — and it looks favourable right up until it is not. */
  const defaultThrough = useMemo(() => {
    const nowP = new Date().toISOString().slice(0, 7);
    const idx = months.findIndex((m) => m >= nowP);
    return idx <= 0 ? 0 : idx - 1;
  }, [months]);
  const [through, setThrough] = useState<number | null>(null);
  const throughMonth = through ?? defaultThrough;

  const report = useMemo(
    () => (budgets ? buildBudgetReport(fy, throughMonth, budgets, gl.accounts, gl.journals, months) : null),
    [fy, throughMonth, budgets, gl.accounts, gl.journals, months],
  );

  const budgetable = useMemo(
    () => gl.accounts
      .filter((a) => a.type === 'income' || a.type === 'expense')
      .filter((a) => a.isPostable !== false)
      .sort((a, b) => a.code.localeCompare(b.code)),
    [gl.accounts],
  );

  function startEditing() {
    const d: Record<string, string> = {};
    for (const a of budgetable) {
      const b = (budgets ?? []).find((x) => x.fy === fy && x.accountCode === a.code);
      d[a.code] = b ? String(b.annual) : '';
    }
    setDraft(d);
    setEditing(true);
  }

  async function save() {
    setSaving(true);
    try {
      for (const [code, value] of Object.entries(draft)) {
        const annual = Number(value);
        const id = `${fy}_${code}`;
        if (!value.trim() || !Number.isFinite(annual) || annual === 0) continue;
        await setDoc(doc(db, BUDGET_COLLECTION, id), {
          id, fy, accountCode: code, annual,
          updatedAt: new Date().toISOString(),
          updatedBy: userData?.uid ?? '',
        } satisfies AccountBudget, { merge: true });
      }
      setEditing(false);
    } finally {
      setSaving(false);
    }
  }

  if (budgets === null || gl.loading) {
    return <div className="p-6 flex justify-center"><Spinner /></div>;
  }

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto">
      <PageHeader
        icon={Target} tone="navy"
        title="Budgets"
        subtitle="Measured against the year so far, not against the annual figure"
        actions={(
          <div className="flex gap-2">
            <select value={fy} onChange={(e) => setFy(e.target.value)}
              className={glassFieldClass('max-w-[120px] text-sm')}>
              {[1, 0, -1].map((n) => {
                const y = thisYear - n;
                return <option key={y} value={`FY${y}`}>{`FY${y}`}</option>;
              })}
            </select>
            {editing ? (
              <>
                <button type="button" className={btnGhost} onClick={() => setEditing(false)}>
                  Cancel
                </button>
                <button type="button" className={btnPrimary} onClick={save} disabled={saving}>
                  <Save size={15} /> {saving ? 'Saving…' : 'Save budgets'}
                </button>
              </>
            ) : (
              <button type="button" className={btnPrimary} onClick={startEditing}>
                Set budgets
              </button>
            )}
          </div>
        )}
      />

      {editing ? (
        <SoftCard pad="p-0">
          <PanelHeader icon={Target} tone="navy" title={`Annual budgets for ${fy}`}
            subtitle="One figure per account. Leave blank for an account with no budget." />
          <div className="max-h-[560px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white dark:bg-gray-800">
                <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400 border-b border-gray-100 dark:border-gray-700/70">
                  <th className="py-2 pl-4 pr-2 font-semibold">Account</th>
                  <th className="px-2 font-semibold">Type</th>
                  <th className="px-2 pr-4 font-semibold text-right w-40">Annual budget</th>
                </tr>
              </thead>
              <tbody>
                {budgetable.map((a) => (
                  <tr key={a.code} className="border-b border-gray-50 dark:border-gray-800">
                    <td className="py-1.5 pl-4 pr-2">
                      <span className="font-mono text-xs text-gray-500">{a.code}</span>{' '}
                      <span className="text-gray-800 dark:text-gray-100">{a.name}</span>
                    </td>
                    <td className="px-2 text-gray-500 text-xs">{a.type}</td>
                    <td className="px-2 pr-4">
                      <input type="number" step="0.01" value={draft[a.code] ?? ''}
                        onChange={(e) => setDraft({ ...draft, [a.code]: e.target.value })}
                        className={glassFieldClass('text-right')} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="px-4 py-3 border-t border-gray-100 dark:border-gray-700 text-[12px] text-gray-500">
            An annual figure is spread evenly across the year unless the school phases it.
            Absent and phased-flat are the same thing, so there is no second state to keep
            in step.
          </p>
        </SoftCard>
      ) : report && (
        <>
          <div className="flex items-center gap-2 mb-4">
            <span className="text-[11px] uppercase tracking-wide text-gray-400">Through</span>
            <select value={throughMonth} onChange={(e) => setThrough(Number(e.target.value))}
              className={glassFieldClass('max-w-[200px]')}>
              {months.map((m, i) => (
                <option key={m} value={i}>{periodLabel(m)}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-5">
            <StatChip tone="slate" value={money(report.expense.budget)} label="Should have spent" />
            <StatChip tone={report.expense.variance > 0 ? 'rose' : 'emerald'}
              icon={report.expense.variance > 0 ? TrendingUp : TrendingDown}
              value={money(report.expense.actual)} label="Actually spent" />
            <StatChip tone="slate" value={money(report.income.budget)} label="Should have earned" />
            <StatChip tone={report.income.variance >= 0 ? 'emerald' : 'amber'}
              value={money(report.income.actual)} label="Actually earned" />
          </div>

          {report.lines.length === 0 ? (
            <SoftCard>
              <p className="text-sm text-gray-500 text-center py-6">
                No budgets set for {fy}. Nothing can be measured against a plan that does
                not exist.
              </p>
            </SoftCard>
          ) : (
            <>
              {report.overspent.length > 0 && (
                <SoftCard className="mb-4 border-l-4 border-amber-400">
                  <p className="text-sm text-amber-800 dark:text-amber-300 mb-1">
                    <AlertTriangle size={15} className="inline mr-1 -mt-0.5" />
                    {report.overspent.length} account{report.overspent.length === 1 ? ' is' : 's are'}
                    {' '}running against the plan at this point in the year.
                  </p>
                  <p className="text-[12px] text-gray-600 dark:text-gray-300">
                    Measured against {periodLabel(months[throughMonth])} — not the annual
                    figure, which nothing has breached yet.
                  </p>
                </SoftCard>
              )}

              <SoftCard pad="p-0">
                <PanelHeader icon={Target} tone="navy" title="By account"
                  subtitle={`Budget to the end of ${periodLabel(months[throughMonth])}`} />
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400 border-b border-gray-100 dark:border-gray-700/70">
                        <th className="py-2 pl-4 pr-2 font-semibold">Account</th>
                        <th className="px-2 font-semibold text-right">Budget to date</th>
                        <th className="px-2 font-semibold text-right">Actual</th>
                        <th className="px-2 font-semibold text-right">Variance</th>
                        <th className="px-2 pr-4 font-semibold text-right">Annual used</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.lines.map((l) => <Line key={l.accountCode} l={l} />)}
                    </tbody>
                  </table>
                </div>
              </SoftCard>

              {report.unbudgeted.length > 0 && (
                <SoftCard pad="p-0" className="mt-5">
                  <PanelHeader icon={Info} tone="navy" title="Spent with no budget"
                    subtitle="Not an error — but it separates a budget that was wrong from one that was ignored" />
                  <table className="w-full text-sm">
                    <tbody>
                      {report.unbudgeted.map((u) => (
                        <tr key={u.accountCode} className="border-b border-gray-50 dark:border-gray-800">
                          <td className="py-2 pl-4 pr-2">
                            <span className="font-mono text-xs text-gray-500">{u.accountCode}</span>{' '}
                            <span className="text-gray-800 dark:text-gray-100">{u.accountName}</span>
                          </td>
                          <td className="py-2 pr-4 text-right tabular-nums">{money(u.actual)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </SoftCard>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

function Line({ l }: { l: BudgetLine }) {
  /* The colour comes from `favourable`, never from the sign — over on
     income and under on an expense are both good news. */
  const tone = l.favourable
    ? 'text-emerald-700 dark:text-emerald-400'
    : 'text-red-700 dark:text-red-400';

  return (
    <tr className="border-b border-gray-50 dark:border-gray-800">
      <td className="py-2 pl-4 pr-2">
        <span className="font-mono text-xs text-gray-500">{l.accountCode}</span>{' '}
        <span className="text-gray-800 dark:text-gray-100">{l.accountName}</span>
        <span className="block text-[11px] text-gray-400">
          {l.type} · annual {money(l.annual)}
        </span>
      </td>
      <td className="px-2 text-right tabular-nums text-gray-600 dark:text-gray-300">
        {money(l.budgetToDate)}
      </td>
      <td className="px-2 text-right tabular-nums">{money(l.actualToDate)}</td>
      <td className={`px-2 text-right tabular-nums font-medium ${tone}`}>
        {l.variance > 0 ? '+' : ''}{money(l.variance)}
        <span className="block text-[11px] font-normal opacity-70">
          {l.variancePct > 0 ? '+' : ''}{l.variancePct}%
        </span>
      </td>
      <td className="px-2 pr-4 text-right tabular-nums text-gray-500">
        {l.usedPct}%
      </td>
    </tr>
  );
}
